import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue } from 'jotai';
import isEqual from 'lodash/isEqual';

import { useTranslations } from '~/shared/i18n/useTranslations';
import { $user } from '~/shared/providers/auth/identity.localState';
import {
  FileFormData,
  FileMutations,
  FileUploadState,
  GenericFile,
  UploadErrorActions,
} from '~/shared/types/file';
import {
  createPreviewUrl,
  generateUniqueFileName,
  revokePreviewUrl,
} from '~/shared/utils/fileHelpers';
import {
  uploadFileToS3,
  UploadProgress,
  validateFile,
} from '~/shared/utils/fileUpload';

export type UseFileUploadOptions = {
  entityId?: string;
  mutations: FileMutations;
  files: GenericFile[];
  onFilesChange?: (files: GenericFile[]) => void;
  onAllUploadsComplete?: () => void;
  defaultMetadata?: Record<string, unknown>;
  mimeType?: string;
};

export type UseFileUploadReturn = {
  localFiles: FileFormData[];
  isCreating: boolean;
  isConfirming: boolean;
  isDeleting: boolean;
  handleFileSelection: (files: File[]) => Promise<void>;
  removeFile: (id: string) => Promise<void>;
  retryUpload: (fileId: string) => Promise<void>;
  dismissError: (fileId: string) => void;
  uploadErrorActions: UploadErrorActions;
  previewUrlsRef: React.MutableRefObject<Set<string>>;
};

export const useFileUpload = ({
  entityId,
  mutations,
  files,
  onFilesChange,
  onAllUploadsComplete,
  defaultMetadata,
  mimeType,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const toast = useToast();
  const t = useTranslations();
  const user = useAtomValue($user);

  const [localFiles, setLocalFiles] = React.useState<FileFormData[]>([]);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isConfirming, setIsConfirming] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const prevFilesRef = React.useRef<GenericFile[]>([]);
  const previewUrlsRef = React.useRef<Set<string>>(new Set());

  const hasFilesChanged = React.useCallback((newFiles: GenericFile[]) => {
    const prev = prevFilesRef.current;

    if (!prev || prev.length !== newFiles.length) return true;

    const hasCriticalChanges = newFiles.some(
      (file, i) =>
        prev[i].id !== file.id ||
        prev[i].status !== file.status ||
        prev[i].fileName !== file.fileName,
    );

    if (hasCriticalChanges) return true;

    return !isEqual(prev, newFiles);
  }, []);

  const updateFileState = React.useCallback(
    (trackingId: string, updates: Partial<FileFormData>) => {
      setLocalFiles((prev) =>
        prev.map((file) =>
          file.trackingId === trackingId ? { ...file, ...updates } : file,
        ),
      );
    },
    [],
  );

  const handleUpload = async (
    tempId: string,
    file: File,
    uniqueFileName: string,
  ) => {
    if (!entityId) return;

    updateFileState(tempId, {
      uploadState: 'uploading',
      uploadProgress: 0,
    });

    try {
      setIsCreating(true);

      const fileResult = await mutations.createFile({
        fileName: uniqueFileName,
        mimeType: file.type || 'application/octet-stream',
        entityId,
        fileSize: file.size,
        metadata: defaultMetadata,
      });

      const { id: fileId, uploadUrl } = fileResult;

      updateFileState(tempId, {
        id: fileId,
        uploadUrl,
      });

      setIsCreating(false);

      await uploadFileToS3(file, uploadUrl, {
        onProgress: (progress: UploadProgress) => {
          updateFileState(tempId, {
            uploadProgress: progress.percentage,
          });
        },
        maxRetries: 3,
        retryDelay: 1000,
        fileId,
        uploaderId: user?.id || 'unknown',
      });

      updateFileState(tempId, {
        uploadState: 'uploaded',
        uploadProgress: 100,
      });

      setIsConfirming(true);

      const confirmResult = await mutations.confirmUpload({ fileId });

      const hasRealUrl = confirmResult.url && confirmResult.url.length > 0;
      const currentFile = localFiles.find((f) => f.trackingId === tempId);
      if (hasRealUrl && currentFile?.previewUrl) {
        revokePreviewUrl(currentFile.previewUrl, previewUrlsRef.current);
      }

      updateFileState(tempId, {
        downloadUrl: hasRealUrl ? confirmResult.url : undefined,
        status: 'Active',
        uploadState: 'confirmed',
        uploadProgress: undefined,
        ...(hasRealUrl ? { previewUrl: undefined } : {}),
      });

      setIsConfirming(false);

      toast.success(t.files.uploadSuccess(file.name));

      if (onFilesChange) {
        const newFile: GenericFile = {
          id: fileId,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          downloadUrl: confirmResult.url,
          status: 'Active',
          fileSize: file.size,
        };
        const updatedFiles = [...files, newFile];
        onFilesChange(updatedFiles);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed';

      updateFileState(tempId, {
        uploadState: 'error',
        error: errorMessage,
        uploadProgress: undefined,
      });

      throw error;
    } finally {
      setIsCreating(false);
      setIsConfirming(false);
    }
  };

  const removeFile = async (id: string) => {
    const file = localFiles.find((f) => f.id === id);
    if (!file) return;

    revokePreviewUrl(file.previewUrl, previewUrlsRef.current);

    try {
      setIsDeleting(true);

      if (file.status === 'Active' && !id.startsWith('temp-')) {
        await mutations.deleteFile({ fileId: id });
        toast.success(t.files.deleteSuccess(file.fileName));

        if (onFilesChange) {
          const updatedFiles = files.filter((f) => f.id !== id);
          onFilesChange(updatedFiles);
        }
      } else {
        setLocalFiles((prev) => prev.filter((f) => f.id !== id));
      }
    } catch (error) {
      toast.error(
        t.files.deleteFailed(
          file.fileName,
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadRef = React.useRef(handleUpload);

  const retryUpload = React.useCallback(
    async (fileId: string) => {
      const file = localFiles.find(
        (f) => (f.id === fileId || f.trackingId === fileId) && f.file,
      );
      if (!file || !file.file) return;

      const trackingId = file.trackingId || file.id;
      try {
        await handleUploadRef.current(trackingId, file.file, file.fileName);
        onAllUploadsComplete?.();
      } catch {
        // Error is already handled in handleUpload
      }
    },
    [localFiles, onAllUploadsComplete],
  );

  const dismissError = React.useCallback((fileId: string) => {
    setLocalFiles((prev) => {
      const file = prev.find((f) => f.id === fileId || f.trackingId === fileId);
      if (file?.previewUrl) {
        revokePreviewUrl(file.previewUrl, previewUrlsRef.current);
      }
      return prev.filter((f) => f.id !== fileId && f.trackingId !== fileId);
    });
  }, []);

  const uploadErrorActions = React.useMemo<UploadErrorActions>(
    () => ({
      retryUpload,
      dismissError,
    }),
    [retryUpload, dismissError],
  );

  const handleFileSelection = async (selectedFiles: File[]) => {
    if (!entityId) {
      toast.error(t.files.saveFirst);
      return;
    }

    const validatedFiles: Array<{
      file: File;
      tempId: string;
      uniqueFileName: string;
      fileData: FileFormData;
    }> = [];

    for (const file of selectedFiles) {
      const validation = validateFile(file, mimeType);
      if (!validation.isValid) {
        toast.error(`${file.name}: ${validation.error}`);
        continue;
      }

      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const uniqueFileName = generateUniqueFileName(file.name);
      const previewUrl = createPreviewUrl(file, previewUrlsRef.current);

      const fileData: FileFormData = {
        id: tempId,
        trackingId: tempId,
        fileName: uniqueFileName,
        mimeType: file.type || 'application/octet-stream',
        file,
        status: 'PendingUpload',
        uploadState: 'pending',
        fileSize: file.size,
        metadata: defaultMetadata,
        previewUrl,
      };

      validatedFiles.push({ file, tempId, uniqueFileName, fileData });
    }

    if (validatedFiles.length > 0) {
      setLocalFiles((prev) => [
        ...prev,
        ...validatedFiles.map((vf) => vf.fileData),
      ]);

      const uploadPromises = validatedFiles.map(
        async ({ file, tempId, uniqueFileName }) => {
          try {
            await handleUpload(tempId, file, uniqueFileName);
          } catch (error) {
            updateFileState(tempId, {
              uploadState: 'error',
              error: error instanceof Error ? error.message : 'Upload failed',
            });
            toast.error(t.files.uploadFailed(file.name));
          }
        },
      );

      await Promise.all(uploadPromises);

      onAllUploadsComplete?.();
    }
  };

  // Cleanup preview URLs on unmount
  React.useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  // Keep handleUploadRef current
  React.useEffect(() => {
    handleUploadRef.current = handleUpload;
  });

  // Sync local files with remote files
  React.useEffect(() => {
    if (!files || !hasFilesChanged(files)) return;

    prevFilesRef.current = files;

    setLocalFiles((prev) => {
      const prevFileById = new Map(prev.map((f) => [f.id, f]));

      const mappedFiles = files.map((file, index) => {
        const fileId = file.id || `existing-${index}`;
        const prevFile = prevFileById.get(fileId);

        const downloadUrl =
          file.downloadUrl || prevFile?.downloadUrl || prevFile?.previewUrl;

        const hasRealServerUrl =
          file.downloadUrl && file.downloadUrl.length > 0;
        if (hasRealServerUrl && prevFile?.previewUrl) {
          revokePreviewUrl(prevFile.previewUrl, previewUrlsRef.current);
        }

        return {
          id: fileId,
          fileName: file.fileName,
          mimeType: file.mimeType,
          downloadUrl,
          status: file.status,
          uploadState: (file.status === 'Active'
            ? 'confirmed'
            : 'pending') as FileUploadState,
          fileSize: file.fileSize,
          metadata: file.metadata,
          createdAt: file.createdAt,
          updatedAt: file.updatedAt,
          previewUrl: hasRealServerUrl ? undefined : prevFile?.previewUrl,
        };
      });

      const localOnlyFiles = prev.filter(
        (localFile) =>
          localFile.id.startsWith('temp-') &&
          (localFile.uploadState === 'uploading' ||
            localFile.uploadState === 'pending' ||
            localFile.uploadState === 'error'),
      );
      return [...mappedFiles, ...localOnlyFiles];
    });
  }, [files, hasFilesChanged]);

  return {
    localFiles,
    isCreating,
    isConfirming,
    isDeleting,
    handleFileSelection,
    removeFile,
    retryUpload,
    dismissError,
    uploadErrorActions,
    previewUrlsRef,
  };
};
